import { NetworkErrorService } from "@app/services/NetworkError.service";
import { SpinnerService } from "@app/services/spinner.service";
import { PaymentStore } from "@app/services/stores/PaymentStore";
import { finalize, take, takeUntil } from "rxjs/operators";
import { Component, Inject, OnDestroy, OnInit } from "@angular/core";
import { Router } from "@angular/router";
import { Property } from "@app/models/Property";
import { UserStore } from "@app/services/stores/UserStore";
import { PaymentSchedule } from "@app/models/PaymentSchedule";
import { PaymentSuccessInfo } from "@app/models/PaymentSuccessInfo";
import { PaymentScheduleApi } from "@app/services/apiServices/PaymentScheduleApi";
import { PaymentErrorDialog } from "@app/components/sharedDialogs/dialogPaymentError.component";
import { MatDialog } from "@angular/material/dialog";
import { Subject } from "rxjs";
import { UtilService } from "@app/services/Util.service";

@Component({
    selector: "app-future-payment-confirmation",
    template: require("./future-payment-confirmation.component.html"),
})
export class FuturePaymentConfirmationComponent implements OnInit, OnDestroy {
    paymentScheduleRequest: PaymentSchedule;
    paymentSuccessInfo: PaymentSuccessInfo;
    property: Property;
    tcCheckbox: boolean;
    disableSubmitBtn: boolean = false;
    convenienceFee: number;
    $stateParams: any;

    private destroy$ = new Subject<void>();

    constructor(
        @Inject(MatDialog) private dialog: MatDialog,
        @Inject(PaymentScheduleApi) private paymentScheduleApi: PaymentScheduleApi,
        @Inject(SpinnerService) private spinnerService: SpinnerService,
        @Inject(NetworkErrorService) private networkErrorService: NetworkErrorService,
        @Inject(PaymentStore) private paymentStore: PaymentStore,
        @Inject(UtilService) private utilService: UtilService,
        @Inject(UserStore) private userStore: UserStore,
        @Inject(Router) private router: Router) {

        if(this.router.getCurrentNavigation()) {
            this.$stateParams = this.router.getCurrentNavigation().extras.state;
        }
    }

    ngOnInit() {
        if (this.$stateParams?.paymentScheduleRequest) {
            this.paymentScheduleRequest = this.$stateParams.paymentScheduleRequest;
        } else if (this.paymentStore.getPaymentScheduleRequest()) {
            this.paymentScheduleRequest = this.paymentStore.getPaymentScheduleRequest();
        } else {
            this.router.navigateByUrl('/properties');
        }

        if (this.$stateParams?.convenienceFee) {
            this.convenienceFee = this.$stateParams.convenienceFee;
        } else if (this.paymentStore.getConvenienceFee()) {
            this.convenienceFee = this.paymentStore.getConvenienceFee();
        }

        if (this.$stateParams?.property) {
            this.property = this.$stateParams.property;
        } else if (this.paymentStore.getProperty()) {
            this.property = this.paymentStore.getProperty();
        }
    }

    submit() {
        this.disableSubmitBtn = true;
        this.spinnerService.showSpinner();
        this.paymentScheduleRequest.acceptedAmounts = this.tcCheckbox;
        this.paymentScheduleRequest.acceptedAmountsDate = new Date().toISOString().slice(0, 10);

        // paymentMethod is not needed in paymentScheduleRequest
        delete this.paymentScheduleRequest.paymentMethod;

        const userEmail = this.userStore.getEmail()

        this.paymentScheduleApi
            .paymentSchedulesCreatePost(this.paymentScheduleRequest)
            .pipe(
                takeUntil(this.destroy$),
                finalize(() => this.spinnerService.closeSpinner())
            ).subscribe({
                next: (resp) => {
                    if (resp.id) {
                        this.paymentSuccessInfo = {
                            paymentId: resp.id,
                            paymentType: "schedule",
                            paymentScheduleRequest: this.paymentScheduleRequest,
                            totalPaymentAmount: resp.futurePayments[0].total,
                            paymentDate: resp.startDate,
                            email: userEmail,
                            confirmationNumber: resp.confirmationNumber,
                            property: this.property,
                            scheduleInfo: {
                                scheduleName: resp.nickname,
                                endDate: resp.endDate,
                                frequency: this.toTitleCase(resp.frequency),
                                numberOfPayments: resp.remainingPayments,
                            }
                        };
                        this.paymentStore.clearAll();
                        this.paymentStore.setPaymentSuccessInfo(this.paymentSuccessInfo)
                        this.router.navigateByUrl('/properties/' + this.paymentSuccessInfo.property.id + '/paymentSuccess',
                            { state: { propertyId: this.paymentSuccessInfo.property.id } });
                    } else {
                        throw new Error("Unexpected error behind gateway when creating your schedule. Please reach out to First Citizens Bank if this error persists.")
                    }
                },
                error: (err: any) => this.handlePaymentError(err)
        });
    }

    goToHOAPay() {
        this.router.navigateByUrl('/properties');
    }

    private handlePaymentError(err: any) {
        if (err.status === 409) {
            this.networkErrorService.handleNetworkError(err, true)
                .pipe(
                    take(1),
                    takeUntil(this.destroy$)
                )
                .subscribe((resp: string | boolean)=> {
                    if (resp === true) {
                        this.submit();
                    }
                });
        } else {
            this.dialog.open(PaymentErrorDialog)
                .afterClosed()
                .pipe(
                    take(1),
                    takeUntil(this.destroy$)
                )
                .subscribe(() => this.goToHOAPay());
        }
    }

    ngOnDestroy(){
        this.destroy$.next();
        this.destroy$.complete();
    }

    toTitleCase(string) {
        return this.utilService.toTitleCase(string);
    }
}

